import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PencilSimple, Plus, X, Check, ArrowsClockwise, Robot, Clock, CheckCircle, XCircle } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { fetchUsers, createUser, updateUser, deleteUser, syncCoordinador, getSyncStatus, updateSyncConfig } from "../lib/api";
import { toast } from "sonner";

const EMPTY_FORM = {
  email: "",
  nombre: "",
  password: "",
  role: "coordinador",
  pais: "es",
  id_consultor: "",
  hesttya_email: "",
  hesttya_password: "",
  active: true,
};

export default function Usuarios() {
  const navigate = useNavigate();
  const { isSuperadmin, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // user id being edited
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncAllProgress, setSyncAllProgress] = useState(null); // { done, total, current }
  const [autoSyncStatus, setAutoSyncStatus] = useState(null);
  const [togglingAuto, setTogglingAuto] = useState(false);

  const loadAutoSyncStatus = useCallback(async () => {
    try { setAutoSyncStatus(await getSyncStatus()); } catch {}
  }, []);

  useEffect(() => { if (isSuperadmin) loadAutoSyncStatus(); }, [isSuperadmin, loadAutoSyncStatus]);

  const handleToggleAutoSync = async () => {
    if (!autoSyncStatus) return;
    setTogglingAuto(true);
    try {
      const newEnabled = !autoSyncStatus.enabled;
      await updateSyncConfig({ enabled: newEnabled });
      await loadAutoSyncStatus();
      toast.success(newEnabled ? "Actualización automática activada" : "Actualización automática desactivada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar la configuración");
    } finally {
      setTogglingAuto(false);
    }
  };

  // Redirect non-superadmins
  useEffect(() => {
    if (!authLoading && !isSuperadmin) {
      navigate("/", { replace: true });
    }
  }, [authLoading, isSuperadmin, navigate]);

  const load = async () => {
    try {
      setLoadingUsers(true);
      const data = await fetchUsers();
      setUsers(data);
    } catch {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (isSuperadmin) load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u.id);
    setForm({
      email: u.email,
      nombre: u.nombre,
      password: "",
      role: u.role,
      pais: u.pais ?? "es",
      id_consultor: u.id_consultor ?? "",
      hesttya_email: u.hesttya_email ?? "",
      hesttya_password: "",
      active: u.active,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailLower = form.email.toLowerCase().trim();
    const duplicate = users.find(
      (u) => u.email === emailLower && u.id !== editing
    );
    if (duplicate) {
      toast.error("Ya existe un usuario con ese email");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        email: emailLower,
        id_consultor: form.id_consultor !== "" ? parseInt(form.id_consultor, 10) : null,
      };
      if (!payload.password) delete payload.password;
      if (editing) {
        await updateUser(editing, payload);
        toast.success("Usuario actualizado");
      } else {
        await createUser(payload);
        toast.success("Usuario creado");
      }
      closeForm();
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al guardar usuario";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.nombre}? Esta acción no se puede deshacer.`)) return;
    const prev = users;
    setUsers((list) => list.filter((x) => x.id !== u.id));
    try {
      await deleteUser(u.id);
      toast.success("Usuario eliminado");
      await load();
    } catch (err) {
      setUsers(prev);
      const msg = err.response?.data?.detail || "Error al eliminar usuario";
      toast.error(msg);
    }
  };

  const handleSyncAll = async () => {
    const targets = users.filter((u) => u.hesttya_email && u.active);
    if (targets.length === 0) {
      toast.info("No hay usuarios con credenciales Hesttya configuradas");
      return;
    }
    setSyncingAll(true);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < targets.length; i++) {
      const u = targets[i];
      setSyncAllProgress({ done: i, total: targets.length, current: u.nombre });
      try {
        const r = await syncCoordinador(u.id, year, month);
        toast.success(`${u.nombre} · ${r.count} consultoras · ${r.label}`, { duration: 4000 });
        ok++;
      } catch (err) {
        const msg = err.response?.data?.detail || "Error al sincronizar";
        toast.error(`${u.nombre} · ${msg}`, { duration: 5000 });
        fail++;
      }
    }
    setSyncingAll(false);
    setSyncAllProgress(null);
    if (fail === 0) {
      toast.success(`Sincronización completa · ${ok} coordinador${ok !== 1 ? "as" : "a"} actualizad${ok !== 1 ? "as" : "a"}`);
    } else {
      toast.warning(`Terminado · ${ok} correctas, ${fail} con error${fail !== 1 ? "es" : ""}`);
    }
  };

  if (authLoading || loadingUsers) {
    return (
      <div className="flex items-center justify-center h-64 text-[#736F68] text-sm">
        Cargando…
      </div>
    );
  }

  return (
    <div className="px-6 py-8">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#2D2A26] font-display">Usuarios</h1>
          <p className="text-sm text-[#736F68] mt-0.5">Gestión de accesos al panel</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSyncAll}
            disabled={syncingAll}
            className="flex items-center gap-2 text-sm font-semibold rounded-lg px-4 py-2.5 border border-[#E5E0D8] hover:bg-[#F2EFEA] text-[#736F68] hover:text-[#2D2A26] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Sincronizar todos los coordinadores con Hesttya"
          >
            <ArrowsClockwise size={16} className={syncingAll ? "animate-spin" : ""} />
            {syncingAll && syncAllProgress
              ? `${syncAllProgress.done}/${syncAllProgress.total} · ${syncAllProgress.current}`
              : "Sincronizar todos"}
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-[#C57B67] hover:bg-[#B06A57] text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors"
          >
            <Plus size={16} weight="bold" />
            Nuevo usuario
          </button>
        </div>
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold text-[#2D2A26] font-display">
                {editing ? "Editar usuario" : "Nuevo usuario"}
              </h2>
              <button onClick={closeForm} className="text-[#736F68] hover:text-[#2D2A26]">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Field label="Nombre">
                <input
                  name="nombre"
                  value={form.nombre}
                  onChange={handleChange}
                  required
                  placeholder="Nombre completo"
                  className={inputCls}
                />
              </Field>
              <Field label="Email">
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="email@ejemplo.com"
                  className={inputCls}
                />
              </Field>
              <Field label={editing ? "Nueva contraseña (dejar vacío para no cambiar)" : "Contraseña"}>
                <input
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  required={!editing}
                  placeholder="••••••••"
                  className={inputCls}
                />
              </Field>
              <Field label="Rol">
                <select name="role" value={form.role} onChange={handleChange} className={inputCls}>
                  <option value="coordinador">Coordinador</option>
                  <option value="admin">Administrador</option>
                  <option value="marketing">Marketing</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </Field>
              <Field label="País">
                <select name="pais" value={form.pais} onChange={handleChange} className={inputCls}>
                  <option value="es">España</option>
                  <option value="pt">Portugal</option>
                </select>
              </Field>
              <Field label="ID Consultor (solo coordinadores)">
                <input
                  name="id_consultor"
                  type="number"
                  value={form.id_consultor}
                  onChange={handleChange}
                  placeholder="ID numérico (opcional)"
                  className={inputCls}
                />
              </Field>
              <div className="border-t border-[#E5E0D8] pt-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#736F68] mb-3">
                  Credenciales Hesttya (para sincronización)
                </p>
                <div className="space-y-3">
                  <Field label="Email en app.hesttya.pt">
                    <input
                      name="hesttya_email"
                      type="email"
                      value={form.hesttya_email}
                      onChange={handleChange}
                      placeholder="usuario@hesttya.com"
                      className={inputCls}
                    />
                  </Field>
                  <Field label={editing ? "Contraseña Hesttya (dejar vacío para no cambiar)" : "Contraseña en app.hesttya.pt"}>
                    <input
                      name="hesttya_password"
                      type="password"
                      value={form.hesttya_password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className={inputCls}
                    />
                  </Field>
                </div>
              </div>
              {editing && (
                <label className="flex items-center gap-2 text-sm text-[#2D2A26]">
                  <input
                    name="active"
                    type="checkbox"
                    checked={form.active}
                    onChange={handleChange}
                    className="rounded border-[#E5E0D8] text-[#C57B67] focus:ring-[#C57B67]"
                  />
                  Cuenta activa
                </label>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="text-sm text-[#736F68] hover:text-[#2D2A26] px-4 py-2 rounded-lg border border-[#E5E0D8] transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 bg-[#C57B67] hover:bg-[#B06A57] disabled:opacity-60 text-white text-sm font-semibold rounded-lg px-4 py-2 transition-colors"
                >
                  <Check size={16} weight="bold" />
                  {submitting ? "Guardando…" : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#E5E0D8] overflow-x-auto">
        {users.length === 0 ? (
          <p className="text-center text-[#736F68] text-sm py-12">No hay usuarios registrados.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E0D8] bg-[#F9F8F6]">
                <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-[#736F68]">
                  Nombre
                </th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-[#736F68]">
                  Email
                </th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-[#736F68]">
                  Rol
                </th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-[#736F68]">
                  ID Consultor
                </th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-[#736F68]">
                  Hesttya
                </th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-[#736F68]">
                  Estado
                </th>
                <th className="px-4 py-3 w-28" />
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr
                  key={u.id}
                  className={`border-b border-[#E5E0D8] last:border-0 ${
                    i % 2 === 0 ? "" : "bg-[#FAFAF9]"
                  }`}
                >
                  <td className="px-4 py-3 font-medium text-[#2D2A26]">{u.nombre}</td>
                  <td className="px-4 py-3 text-[#736F68] max-w-[200px] truncate">{u.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                        u.role === "superadmin"
                          ? "bg-[#F2EFEA] text-[#C57B67]"
                          : "bg-[#EEF4FF] text-[#4A78C8]"
                      }`}
                    >
                      {u.role === "superadmin" ? "Superadmin" : "Coordinador"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#736F68]">
                    {u.id_consultor ?? <span className="text-[#B0A99F]">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {u.hesttya_email ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 text-green-700">
                        Configurado
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#F9F8F6] text-[#B0A99F]">
                        Sin configurar
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                        u.active
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-600"
                      }`}
                    >
                      {u.active ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => openEdit(u)}
                        className="p-1.5 text-[#736F68] hover:text-[#2D2A26] hover:bg-[#F2EFEA] rounded-lg transition"
                        title="Editar"
                      >
                        <PencilSimple size={15} />
                      </button>
                      <button
                        onClick={() => handleDelete(u)}
                        className="px-2.5 py-1 text-[11px] font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition"
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Panel de automatización diaria */}
      <div className="bg-white border border-[#E5E0D8] rounded-2xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-semibold text-[#2D2A26] text-sm flex items-center gap-2">
              <Robot size={15} weight="duotone" className="text-[#C57B67]" />
              Actualización automática diaria
            </p>
            <p className="text-xs text-[#736F68] mt-0.5">
              Descarga el CSV de cada coordinador desde Hesttya cada noche a las <strong>00:05 hora española</strong>.
            </p>
          </div>
          {autoSyncStatus !== null && (
            <button
              onClick={handleToggleAutoSync}
              disabled={togglingAuto}
              className={`relative shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                autoSyncStatus.enabled ? "bg-[#8A9A86]" : "bg-[#D0CBC3]"
              }`}
              title={autoSyncStatus.enabled ? "Desactivar auto-sync" : "Activar auto-sync"}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                autoSyncStatus.enabled ? "translate-x-5" : "translate-x-0"
              }`} />
            </button>
          )}
        </div>
        {autoSyncStatus && (
          <>
            <div className="flex items-center gap-4 text-xs text-[#736F68] flex-wrap">
              {autoSyncStatus.enabled && autoSyncStatus.next_run && (
                <span className="inline-flex items-center gap-1">
                  <Clock size={12} weight="duotone" />
                  Próxima ejecución:{" "}
                  <strong className="text-[#2D2A26]">
                    {new Date(autoSyncStatus.next_run).toLocaleString("es-ES", {
                      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid",
                    })}
                  </strong>
                </span>
              )}
              {!autoSyncStatus.enabled && (
                <span className="text-[#C57B67]">Desactivada — actívala para sincronizar sin intervención manual</span>
              )}
            </div>
            {autoSyncStatus.history?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#736F68] font-semibold mb-2">Últimas sincronizaciones</p>
                <ul className="space-y-1.5">
                  {autoSyncStatus.history.slice(0, 7).map((entry, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs">
                      {entry.ok
                        ? <CheckCircle size={14} weight="fill" className="text-[#8A9A86] mt-0.5 shrink-0" />
                        : <XCircle size={14} weight="fill" className="text-[#C57B67] mt-0.5 shrink-0" />}
                      <span className="text-[#736F68]">
                        <strong className="text-[#2D2A26]">
                          {new Date(entry.triggered_at).toLocaleString("es-ES", {
                            day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                          })}
                        </strong>
                        {" · "}
                        <span className={entry.trigger === "auto" ? "text-[#8A9A86]" : "text-[#E3A569]"}>
                          {entry.trigger === "auto" ? "automático" : "manual"}
                        </span>
                        {entry.ok ? ` · ${entry.count} consultoras · ${entry.period}` : ` · ${entry.error}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm text-[#2D2A26] bg-white focus:outline-none focus:ring-2 focus:ring-[#C57B67] focus:border-transparent transition";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-[#736F68] mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
